/**
 * shadcn-style Tabs wrapper around Radix Tabs.
 *
 * Phase-2 Wave-2 W2.T2 primitive. The Employees page (W3) uses these tabs
 * for the «Активные · Ожидают активации · Архив» split (Q63 lock); the
 * `value` + `onValueChange` controlled API is REQUIRED by V2-FIX-09 so the
 * parent can sync the active tab to the `?tab=` URL search param via the
 * sibling `use-tab-param` hook.
 *
 * Visual idiom: warm-theme underline pattern (matches design-reference
 * tabs). The active trigger fills the underline with --accent and renders
 * its label in --ink; muted triggers sit at --muted with a transparent
 * underline so the rest of the row stays calm.
 *
 * A11y: Radix supplies `role="tablist"` / `role="tab"` / `role="tabpanel"`,
 * keyboard nav (Left/Right + Home/End), and roving `tabindex`. We only add
 * a focus-visible ring tuned to the warm tokens.
 */
import * as React from 'react';
import * as TabsPrimitive from '@radix-ui/react-tabs';

import { cn } from '@/lib/utils';

const Tabs = TabsPrimitive.Root;

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    className={cn(
      // Touch-target (impeccable audit P2 / WCAG 2.5.5): the list grows to
      // 44px on COARSE pointers so its triggers (also 44px on coarse) stay
      // flush with the bottom rule. `pointer: fine` keeps the 40px density.
      'inline-flex h-10 items-center gap-1 border-b border-line text-sm [@media(pointer:coarse)]:h-11',
      className,
    )}
    {...props}
  />
));
TabsList.displayName = TabsPrimitive.List.displayName;

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(
      // Base — sits flush with the list's bottom border so the active
      // underline appears continuous with the rule. On COARSE pointers the
      // trigger grows to a 44px hit area (impeccable audit P2 / WCAG 2.5.5);
      // `pointer: fine` (mouse/trackpad) keeps the 40px desktop height.
      'relative inline-flex h-10 items-center gap-1.5 px-3 text-sm font-medium text-muted [@media(pointer:coarse)]:h-11',
      'transition-colors duration-short',
      'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 rounded-sm',
      'hover:text-ink',
      // Active — warm-theme underline (2px terracotta) + ink label.
      'data-[state=active]:text-ink',
      'data-[state=active]:after:absolute data-[state=active]:after:inset-x-0 data-[state=active]:after:-bottom-px data-[state=active]:after:h-[2px] data-[state=active]:after:bg-accent',
      'disabled:pointer-events-none disabled:opacity-50',
      className,
    )}
    {...props}
  />
));
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      'mt-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
      className,
    )}
    {...props}
  />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

export { Tabs, TabsList, TabsTrigger, TabsContent };
